import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Component that checks if user needs to complete onboarding
 * Redirects to onboarding if not completed, otherwise renders children
 */
const OnboardingCheck = ({ children }) => {
  const { user, userData, company, loading: authLoading, initialized } = useAuth();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Check if current path is onboarding-related
  const isOnboardingPath = location.pathname.startsWith('/onboarding');
  const isProfilePath = location.pathname === '/complete-profile';
  const driverAllowedPaths = ['/driver-portal', '/driver-vehicle'];
  const isDriverRoute = driverAllowedPaths.some((path) => location.pathname.startsWith(path));
  const isAuthPath = ['/login', '/register', '/forgot-password', '/complete-profile'].includes(location.pathname);
  // Driver application: the invite landing page is public; everything else under
  // /apply is for users with the `applicant` role, who are confined to it.
  const isApplyInvitePath = location.pathname.startsWith('/apply/invite/');
  const isApplyPath = location.pathname === '/apply' || location.pathname.startsWith('/apply/');

  const computeTargetStep = useCallback((status) => {
    const clamp = (step) => {
      if (typeof step !== 'number' || Number.isNaN(step)) {
        return 1;
      }
      return Math.min(Math.max(Math.floor(step), 1), 4);
    };

    if (!status) {
      return 1;
    }

    if (typeof status.currentStep === 'number') {
      return clamp(status.currentStep);
    }

    if (!status.adminSetupComplete) {
      return 1;
    }

    if (!status.companySetupComplete) {
      return 2;
    }

    if (!status.configurationSetupComplete) {
      return 3;
    }

    return status.onboardingComplete ? 4 : 3;
  }, []);

  useEffect(() => {
    if (authLoading || !initialized) return;

    if (!user || !userData) {
      setLoading(false);
      return;
    }

    const status = userData.onboardingStatus || {};
    let targetStep = computeTargetStep(status);

    const hasCompanyProfile = Boolean(company && company.name);
    const hasBasicSetup = Boolean(userData.role) && Boolean(userData.companyId);
    const needsOnboarding = !status.onboardingComplete || !hasCompanyProfile || !hasBasicSetup;

    if (!hasCompanyProfile) {
      targetStep = Math.max(targetStep, 2);
    }

    if (!hasBasicSetup) {
      targetStep = Math.max(targetStep, 1);
    }

    setOnboardingStatus({
      status,
      targetStep,
      needsOnboarding,
      hasCompanyProfile,
      hasBasicSetup
    });
    setLoading(false);
  }, [authLoading, initialized, user, userData, company, computeTargetStep]);

  // Show loading while checking
  if (authLoading || loading || !initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow access to auth pages without onboarding check
  if (isAuthPath && !user) {
    return children;
  }

  // If not authenticated, let ProtectedRoute handle it
  if (!user || !userData) {
    return children;
  }

  // Invite landing page renders for anyone (it explains the "signed in as someone else" case itself).
  if (isApplyInvitePath) {
    return children;
  }

  if (userData.role === 'applicant') {
    if (!isApplyPath) {
      return <Navigate to="/apply" replace />;
    }
    return children;
  }

  const needsProfileCompletion = !userData.firstName?.trim() || !userData.lastName?.trim();

  if (needsProfileCompletion && !isProfilePath) {
    return (
      <Navigate
        to="/complete-profile"
        replace
        state={{ redirectTo: location.pathname }}
      />
    );
  }

  if (!needsProfileCompletion && isProfilePath) {
    const redirectTarget = location.state?.redirectTo || '/dashboard';
    return <Navigate to={redirectTarget} replace />;
  }

  const isDriver = userData.role === 'driver';

  if (isDriver) {
    if (!isDriverRoute) {
      return <Navigate to="/driver-portal" replace />;
    }
    return children;
  }

  // If user needs onboarding and not on onboarding path, redirect to onboarding
  const desiredStartStep = onboardingStatus?.targetStep;
  const currentStartState = location.state?.startStep;

  if (onboardingStatus?.needsOnboarding) {
    if (!isOnboardingPath || currentStartState !== desiredStartStep) {
      return (
        <Navigate
          to="/onboarding"
          replace
          state={{ startStep: desiredStartStep }}
        />
      );
    }
  }

  // If user completed onboarding but on onboarding path, redirect to dashboard
  if (!onboardingStatus?.needsOnboarding && isOnboardingPath) {
    return <Navigate to="/dashboard" replace />;
  }

  // Otherwise, render children
  return children;
};

export default OnboardingCheck;
